import { Clients, useQuery } from "../apollo";
import { SearchUsersQuery } from "../graphql/queries/searchUsers";
import { useEffect, useRef, useState } from "react";
import { USER_SEARCH_LIMIT } from "../constants/user";
import {
  AWSRegions,
  ExtendedUserResponse,
  ExtendedUserSearchResponse,
  UserSearchFilterKeys,
  UserSearchInput,
} from "../generated/ups/graphql";
import { makeCancelable } from "../utils/makeCancelable";

const searchKeys = {
  email: UserSearchFilterKeys.email,
  firstName: UserSearchFilterKeys.firstName,
  lastName: UserSearchFilterKeys.lastName,
} as const;

type UserSearchFilterKeysType = keyof typeof searchKeys;

type CursorTokens = {
  [key in UserSearchFilterKeysType]?: string;
};

type UseUsersSearchType = {
  enabled: boolean;
  value: string;
  region: AWSRegions;
  limit: number;
  transform?: (users: ExtendedUserResponse[]) => unknown;
  cursors?: CursorTokens;
};

type SearchUsersResponseType = {
  searchUsers: ExtendedUserSearchResponse;
};

type UserSearchInputType = {
  userSearch: UserSearchInput;
};

const filters = [
  searchKeys.email,
  searchKeys.firstName,
  searchKeys.lastName,
] as UserSearchFilterKeysType[];

export const useUsersSearch = <TResult = ExtendedUserResponse,>({
  enabled,
  value,
  region,
  limit = USER_SEARCH_LIMIT,
  transform,
}: UseUsersSearchType): {
  data: TResult[] | undefined;
  isError: boolean;
  next: () => Promise<void>;
  prev: () => void;
  page: number;
  isHasNextPage?: boolean;
  isLoading: boolean;
} => {
  const [isLoading, setIsLoading] = useState(false);
  const [users, setUsers] = useState<ExtendedUserResponse[]>([]);
  const [isHasNextPage, setIsHasNextPage] = useState(true);
  const [paginationTokens, setPaginationTokens] = useState<CursorTokens>({});
  const [page, setPage] = useState(0);
  const usersHistory = useRef<ExtendedUserResponse[][]>([]);
  const cancelableRef = useRef<Record<UserSearchFilterKeysType, () => void>>({
    [searchKeys.email]: () => null,
    [searchKeys.firstName]: () => null,
    [searchKeys.lastName]: () => null,
  });
  const history = useRef<
    Record<UserSearchFilterKeysType, { isEmpty: boolean }>
  >({
    [searchKeys.email]: { isEmpty: false },
    [searchKeys.firstName]: { isEmpty: false },
    [searchKeys.lastName]: { isEmpty: false },
  });
  const leftFoundUsers = useRef<ExtendedUserResponse[]>([]);

  const variablesMapper = {
    [searchKeys.email]: (cursor: string | null) => ({
      userSearch: {
        filter: {
          key: searchKeys.email,
          value,
        },
        page: {
          limit,
          cursorToken: cursor,
        },
        region,
      },
    }),
    [searchKeys.firstName]: (cursor: string | null) => ({
      userSearch: {
        filter: {
          key: searchKeys.firstName,
          value,
        },
        page: {
          limit,
          cursorToken: cursor,
        },
        region,
      },
    }),
    [searchKeys.lastName]: (cursor: string | null) => ({
      userSearch: {
        filter: {
          key: searchKeys.lastName,
          value,
        },
        page: {
          limit,
          cursorToken: cursor,
        },
        region,
      },
    }),
  } as const;

  const emailSearch = useQuery<SearchUsersResponseType, UserSearchInputType>(
    Clients.UPS,
    SearchUsersQuery,
    {
      skip: true,
    },
  );

  const firstNameSearch = useQuery<
    SearchUsersResponseType,
    UserSearchInputType
  >(Clients.UPS, SearchUsersQuery, {
    skip: true,
  });

  const lastNameSearch = useQuery<SearchUsersResponseType, UserSearchInputType>(
    Clients.UPS,
    SearchUsersQuery,
    {
      skip: true,
    },
  );

  const filterToSearchMap = {
    [searchKeys.email]: emailSearch,
    [searchKeys.firstName]: firstNameSearch,
    [searchKeys.lastName]: lastNameSearch,
  };

  const getActualFilters = (cursors?: CursorTokens) => {
    const nonEmptyCursors = cursors
      ? Object.entries(cursors).reduce((accum, [key, value]) => {
          if (value) {
            accum[key as keyof CursorTokens] = value;
          }
          return accum;
        }, {} as CursorTokens)
      : {};

    const nonEmptyCursorsKeys = Object.keys(nonEmptyCursors) ?? [];
    const isPaginated = Boolean(cursors && nonEmptyCursorsKeys.length > 0);

    const paginatedFilters = filters.filter((filter) => {
      return nonEmptyCursorsKeys.includes(filter);
    });

    const addRestFilters = (filterItems: UserSearchFilterKeysType[]) => {
      const lastItem = filterItems[filterItems.length - 1];
      const idx = filters.indexOf(lastItem);
      const restFilters = filters.slice(idx + 1);

      return filterItems.concat(restFilters);
    };

    return isPaginated ? addRestFilters(paginatedFilters) : filters;
  };

  const searchUsers = async (cursors?: CursorTokens) => {
    const uniqueUsersList = new Map<string, ExtendedUserResponse>(
      leftFoundUsers.current.map((user) => [user.username, user]),
    );
    const paginationTokensList: CursorTokens = {};
    const searchAppliedFilters = getActualFilters(paginationTokens);

    if (uniqueUsersList.size <= limit) {
      const filteredFilters = searchAppliedFilters.filter(
        (filter) => !history.current[filter].isEmpty,
      );
      const res = await Promise.allSettled(
        filteredFilters.map((filter) => {
          const search = filterToSearchMap[filter];
          const { promise, cancel } = makeCancelable(
            search.refetch(variablesMapper[filter](cursors?.[filter] ?? null)),
          );
          cancelableRef.current[filter] = cancel;

          return promise;
        }),
      );

      res.forEach((result, index) => {
        if (result.status !== "fulfilled") {
          return;
        }
        for (const user of result.value.data.searchUsers.users) {
          if (user.username) {
            uniqueUsersList.set(user.username, user);
          }
        }
        if (result.value.data.searchUsers.users.length === 0) {
          history.current[searchAppliedFilters[index]].isEmpty = true;
        }
        if (result.value.data.searchUsers.nextCursorToken) {
          history.current[searchAppliedFilters[index]].isEmpty = false;
          paginationTokensList[searchAppliedFilters[index]] =
            result.value.data.searchUsers.nextCursorToken;
        }
      });
    }

    const uniquestUsersArray = Array.from(uniqueUsersList.values());
    const usersToDisplay = uniquestUsersArray.slice(0, limit);
    leftFoundUsers.current = uniquestUsersArray.slice(limit);

    return {
      uniqueUsersList: usersToDisplay,
      paginationTokensList,
    };
  };

  const setLeftFoundUsers = () => {
    const usersToDisplay = leftFoundUsers.current.slice(0, limit);
    usersHistory.current[page + 1] = usersToDisplay;
    setUsers(usersToDisplay);
    setPage((prevPage) => prevPage + 1);
    leftFoundUsers.current = leftFoundUsers.current.slice(limit);
  };

  const checkIsHasNextPage = (cursors: CursorTokens) => {
    setIsHasNextPage(
      Boolean(Object.values(cursors)?.length) ||
        Boolean(leftFoundUsers.current.length) ||
        Boolean(usersHistory.current[page + 2]),
    );
  };

  const next = async (cursors: CursorTokens) => {
    if (isLoading || !enabled) {
      return;
    }

    // If user move back and than return to the page that already seen
    if (usersHistory.current[page + 1]) {
      setUsers(usersHistory.current[page + 1]);
      setPage((prevPage) => prevPage + 1);
      checkIsHasNextPage(cursors);
      return;
    }

    // If from previous request we have enough users to display
    if (leftFoundUsers.current.length >= limit) {
      setLeftFoundUsers();
      checkIsHasNextPage(cursors);
      return;
    }

    // If we have no cursors, additionally check if we have users in leftFoundUsers if no return
    if (!cursors) {
      if (leftFoundUsers.current.length) {
        setLeftFoundUsers();
        checkIsHasNextPage(cursors);
        return;
      }

      checkIsHasNextPage(cursors);
      return;
    }

    setIsLoading(true);

    searchUsers(cursors).then((res) => {
      const { uniqueUsersList, paginationTokensList } = res;
      usersHistory.current[page + 1] = uniqueUsersList;

      setPage((prevPage) => prevPage + 1);
      setUsers(uniqueUsersList);
      setPaginationTokens(paginationTokensList);
      checkIsHasNextPage(paginationTokensList);
      setIsLoading(false);
    });
  };

  const prev = () => {
    if (page === 0) {
      return;
    }
    setIsHasNextPage(true);
    setUsers(usersHistory.current[page - 1]);
    setPage((prevPage) => prevPage - 1);
  };

  const resetHistory = () => {
    Object.keys(history.current).forEach((key) => {
      history.current[key as UserSearchFilterKeysType].isEmpty = false;
    });
  };

  const reset = () => {
    cancelableRef.current[searchKeys.email]();
    cancelableRef.current[searchKeys.firstName]();
    cancelableRef.current[searchKeys.lastName]();
    setUsers([]);
    setPaginationTokens({});
    setPage(0);
    resetHistory();
    usersHistory.current = [];
    leftFoundUsers.current = [];
  };

  useEffect(() => {
    reset();

    if (enabled) {
      setIsLoading(true);

      searchUsers()
        .then((res) => {
          const { uniqueUsersList, paginationTokensList } = res;
          usersHistory.current[page] = uniqueUsersList;

          setUsers(uniqueUsersList);
          setPaginationTokens(paginationTokensList);
          checkIsHasNextPage(paginationTokensList);
          setIsLoading(false);
        })
        .catch(() => {
          setIsLoading(false);
        });
    } else {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, value, region]);

  const isError = Boolean(
    users?.length === 0 &&
      !isLoading &&
      enabled &&
      (emailSearch.error || firstNameSearch.error || lastNameSearch.error),
  );

  return {
    data: (transform ? transform(users) : users) as TResult[],
    isError,
    next: () => next(paginationTokens),
    prev,
    page,
    isHasNextPage,
    isLoading,
  };
};
